import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Form, Button, Modal, InputGroup } from 'react-bootstrap';
import '../common-styles.css';
import './LoginPage.css';
import logoFull from '../../assets/images/metalic-logo.png';
import { login as apiLogin, requestAccount, forgotPassword as apiForgotPassword, persistUser } from '../../services/authService';

const LoginPage: React.FC = () => {
  const vantaRef = useRef<HTMLElement>(null);
  const vantaEffectRef = useRef<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [globalRegError, setGlobalRegError] = useState('');

  // Registration form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regAddress, setRegAddress] = useState('');
  const [regCity, setRegCity] = useState('');
  const [regState, setRegState] = useState('');
  const [regCountry, setRegCountry] = useState('');
  const [regPin, setRegPin] = useState('');
  const [regAccountType, setRegAccountType] = useState('');
  const [regReference, setRegReference] = useState('');
  const [regKycDocType, setRegKycDocType] = useState('');
  const [regKycFile, setRegKycFile] = useState<File | null>(null);
  const [regBizDocType, setRegBizDocType] = useState('');
  const [regBizFile, setRegBizFile] = useState<File | null>(null);
  const [regAgree, setRegAgree] = useState(false);
  const [regSubmitted, setRegSubmitted] = useState(false);
  // password strength meter removed
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const markTouched = (field: string) => setTouched(t => (t[field] ? t : { ...t, [field]: true }));

  // Refs for scrolling to first error
  const fieldRefs = {
    name: useRef<HTMLDivElement | null>(null),
    email: useRef<HTMLDivElement | null>(null),
    phone: useRef<HTMLDivElement | null>(null),
    password: useRef<HTMLDivElement | null>(null),
    address: useRef<HTMLDivElement | null>(null),
    city: useRef<HTMLDivElement | null>(null),
    state: useRef<HTMLDivElement | null>(null),
    country: useRef<HTMLDivElement | null>(null),
    pin: useRef<HTMLDivElement | null>(null),
    accountType: useRef<HTMLDivElement | null>(null),
    reference: useRef<HTMLDivElement | null>(null),
    kycType: useRef<HTMLDivElement | null>(null),
    kycFile: useRef<HTMLDivElement | null>(null),
    bizType: useRef<HTMLDivElement | null>(null),
    bizFile: useRef<HTMLDivElement | null>(null),
    agree: useRef<HTMLDivElement | null>(null)
  } as const;

  const strongPasswordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phonePattern = /^[+]?\d{7,15}$/; // basic international numeric phone

  // strength calculation removed

  const regErrors = {
    name: regName ? '' : 'Required',
    email: !regEmail ? 'Required' : (!emailPattern.test(regEmail) ? 'Invalid email' : ''),
    phone: !regPhone ? 'Required' : (!phonePattern.test(regPhone) ? 'Invalid phone' : ''),
    password: !regPassword ? 'Required' : (!strongPasswordPattern.test(regPassword) ? 'Weak password' : ''),
    address: regAddress ? '' : 'Required',
    city: regCity ? '' : 'Required',
    state: regState ? '' : 'Required',
    country: regCountry ? '' : 'Required',
    pin: regPin ? '' : 'Required',
    accountType: regAccountType ? '' : 'Required',
    reference: regReference ? '' : 'Required',
    kycType: regKycDocType ? '' : 'Required',
    kycFile: regKycFile ? '' : 'File required',
    bizType: regBizDocType ? '' : 'Required',
    bizFile: regBizFile ? '' : 'File required',
    agree: regAgree ? '' : 'Must agree'
  } as const;

  const hasRegErrors = Object.values(regErrors).some(Boolean);

  // password strength meter removed

  function scrollToFirstError() {
    const order: (keyof typeof regErrors)[] = ['name','email','phone','password','address','city','state','country','pin','accountType','reference','kycType','kycFile','bizType','bizFile','agree'];
    for (const key of order) {
      if ((regErrors as any)[key]) {
        const ref = (fieldRefs as any)[key] as React.RefObject<HTMLDivElement> | undefined;
        if (ref && ref.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          ref.current.classList.add('shake-error');
          setTimeout(() => ref.current && ref.current.classList.remove('shake-error'), 700);
        }
        break;
      }
    }
  }

  useEffect(() => {
    function initVanta() {
      const w = window as any;
      if (vantaEffectRef.current || !vantaRef.current) return;
      if (!w.VANTA || !w.VANTA.WAVES || !w.THREE) return;
      try {
        vantaEffectRef.current = w.VANTA.WAVES({
          el: vantaRef.current,
          THREE: w.THREE,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.0,
          minWidth: 200.0,
          scale: 1.0,
          scaleMobile: 1.0,
          color: 0x02021f,
        });
      } catch (e) {
        console.error('[LoginPage] Vanta init failed', e);
      }
    }

    function loadScript(src: string) {
      return new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(s);
      });
    }

    let cancelled = false;
    (async () => {
      try {
        if (!(window as any).THREE) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r121/three.min.js');
        }
        if (!(window as any).VANTA || !(window as any).VANTA.WAVES) {
          await loadScript('https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.waves.min.js');
        }
        let tries = 0;
        while (!cancelled && tries < 5 && (!(window as any).THREE || !(window as any).VANTA || !(window as any).VANTA.WAVES)) {
          tries++;
          await new Promise(r => setTimeout(r, 120));
        }
        if (!cancelled) initVanta();
      } catch (e) {
        console.error('Failed to load Vanta/Three', e);
      }
    })();

    return () => {
      cancelled = true;
      if (vantaEffectRef.current) {
        try { vantaEffectRef.current.destroy(); } catch {}
        vantaEffectRef.current = null;
      }
    };
  }, []);

  return (
    <section className="login-page" ref={vantaRef}>
      <div className="login-overlay">
        <Container>
          <Row className="justify-content-center align-items-center min-vh-100">
            <Col xs={12} sm={10} md={8} lg={6} xl={5}>
              <div className="login-top text-center">
                <div className="login-logo">
                  <img src={logoFull} alt="Sun Diam" className="login-top-logo" />
                </div>
                <p className="login-tagline">Illuminate Your Moments with SUN DIAM Diamonds</p>
              </div>
              <div className="login-card">
                <h2 className="login-title text-center">Sign In</h2>
                <Form className="login-form" onSubmit={async e => { 
                  e.preventDefault(); 
                  if (authLoading) return; 
                  setAuthError('');
                  setAuthLoading(true);
                  try {
                    const resp = await apiLogin(username, password);
                    if ((resp as any).data?.user_details) persistUser((resp as any).data.user_details);
                    // redirect placeholder - adjust route as needed
                    window.location.href = '/portal/';
                  } catch (err: any) {
                    setAuthError(err.message || 'Login failed');
                  } finally { setAuthLoading(false); }
                }}>
                  {/* Username / Email Field */}
                  <div className="floating-field">
                    <label htmlFor="username">Username or Email</label>
                    <i className="field-icon fas fa-user" aria-hidden="true"></i>
                    <Form.Control
                      id="username"
                      type="text"
                      autoComplete="username"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      aria-label="Username or Email"
                    />
                  </div>

                  {/* Password Field */}
                  <div className="floating-field has-toggle">
                    <label htmlFor="password">Password</label>
                    <i className="field-icon fas fa-lock" aria-hidden="true"></i>
                    <Form.Control
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      aria-label="Password"
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword(v => !v)}
                    >
                      <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
                    </button>
                  </div>

                  <div className="remember-row d-flex justify-content-end align-items-center mb-3">
                    {/* <Form.Check id="remember" type="checkbox" label="Remember me" /> */}
                    <button type="button" className="link-like small" onClick={() => setShowForgot(true)}>Forgot Password?</button>
                  </div>

          {authError && <div className="field-error mb-2" style={{gridColumn:'1/-1'}}>{authError}</div>}
          <Button type="submit" className="section-btn w-100" disabled={authLoading}>
                    <span className="btn-content">
            {authLoading ? 'Authenticating...' : 'Login'}
                      <i className="fas fa-arrow-right ms-2"></i>
                    </span>
                  </Button>

                  <div className="text-center mt-3">
                    <button type="button" className="link-like" onClick={() => setShowRegister(true)}>New Registration?</button>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
        {/* Forgot Password Modal */}
        <Modal
          show={showForgot}
          onHide={() => setShowForgot(false)}
          centered
          contentClassName="forgot-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Forgot Password?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="forgot-subtitle text-center">Enter your email to reset password</p>
            <Form onSubmit={async e => { e.preventDefault(); if (forgotLoading || !forgotEmail) return; setForgotSuccess(false); setAuthError(''); setForgotLoading(true); try { await apiForgotPassword(forgotEmail); setForgotSuccess(true);} catch (err:any){ setAuthError(err.message || 'Failed to send'); } finally { setForgotLoading(false);} }}>
              <div className={"floating-field is-modal " + (forgotEmail ? 'is-filled' : '')}>
                {/* <label htmlFor="forgot-email">Email</label> */}
                <i className="field-icon far fa-envelope" aria-hidden="true"></i>
                <Form.Control
                  id="forgot-email"
                  type="email"
                  placeholder='Email'
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  aria-label="Email"
                />
              </div>
              {authError && <div className="field-error mb-2 text-center">{authError}</div>}
              <Button type="submit" className="section-btn w-100" disabled={forgotLoading || !forgotEmail}>
                <span className="btn-content">{forgotLoading ? 'Sending...' : 'Proceed'}</span>
              </Button>
              <p className="forgot-hint text-center mt-3">
                {forgotSuccess ? 'An email with a temporary password has been sent.' : 'You will recieve temporary password to your registered email'}
              </p>
              <div className="text-center mt-2">
                
                <button type="button" className="link-like" onClick={() => setShowForgot(false)}>
                  <i className="fas fa-arrow-left me-2"></i> Back to Signin
                </button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
        {/* Registration Modal */}
        <Modal
          show={showRegister}
          onHide={() => setShowRegister(false)}
          centered
          size="lg"
          contentClassName="register-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>New Account Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="forgot-subtitle text-center">Fill in the form below to request for an account from Sun Diam. We will look into the request as soon as possible</p>
      <Form onSubmit={async e => {
        e.preventDefault();
        if (registerLoading) return;
        setRegSubmitted(true);
        setRegisterSuccess(false);
        setGlobalRegError('');
        if (hasRegErrors) { scrollToFirstError(); return; }
        try {
          if (!regKycFile) { setGlobalRegError('KYC document required'); return; }
          setRegisterLoading(true);
          const payload = {
            first_name: regName,
            email: regEmail,
            mobile: regPhone,
            password: regPassword,
            address: regAddress,
            city: regCity,
            state: regState,
            country: regCountry,
            pincode: regPin,
            business_type: regAccountType,
            id_document_type: regKycDocType,
            business_document_type: regBizDocType || undefined,
            notes: regReference || undefined
          };
          await requestAccount(payload, regKycFile, regBizFile || undefined);
          setRegisterSuccess(true);
        } catch (err:any) {
          setGlobalRegError(err.message || 'Registration failed');
        } finally { setRegisterLoading(false); }
      }} className="register-form" noValidate>
              <div className="reg-grid">
                {registerSuccess && <div className="alert-success small mb-2" style={{gridColumn:'1/-1', background:'rgba(16,185,129,0.15)', padding:'6px 8px', borderRadius:4}}>Account request submitted successfully. We will review it shortly.</div>}
                {globalRegError && <div className="field-error" style={{gridColumn:'1/-1'}}>{globalRegError}</div>}
                <div className="first-block">
                {/* Name */}
  <div className="field-wrapper"><div ref={fieldRefs.name} className={"floating-field " + (regName ? 'is-filled' : '') + ((regSubmitted || touched.name) && regErrors.name ? ' error' : '')}>
                  <label htmlFor="reg-name">Name</label>
                  <i className="field-icon fas fa-user"></i>
                  <Form.Control
                    id="reg-name"
                    type="text"
                    value={regName}
                    onChange={e => setRegName(e.target.value)}
                    onBlur={() => markTouched('name')}
                    placeholder=" "
                    aria-label="Name"
          required
                  />
                </div>
  {(regSubmitted || touched.name) && regErrors.name && !['Required'].includes(regErrors.name) && <div className="field-error">{regErrors.name}</div>}</div>
                {/* Phone (moved up to appear on first row with Name) */}
  <div className="field-wrapper"><div ref={fieldRefs.phone} className={"floating-field " + (regPhone ? 'is-filled' : '') + ((regSubmitted || touched.phone) && regErrors.phone ? ' error' : '')}>
                  <label htmlFor="reg-phone">Phone</label>
                  <i className="field-icon fas fa-phone"></i>
                  <Form.Control
                    id="reg-phone"
                    type="tel"
                    value={regPhone}
                    onChange={e => setRegPhone(e.target.value)}
                    onBlur={() => markTouched('phone')}
                    placeholder=" "
                    aria-label="Phone"
          required
                  />
                </div>
  {(regSubmitted || touched.phone) && regErrors.phone && !['Required'].includes(regErrors.phone) && <div className="field-error">{regErrors.phone}</div>}</div>
                {/* Email */}
  <div className="field-wrapper"><div ref={fieldRefs.email} className={"floating-field " + (regEmail ? 'is-filled' : '') + ((regSubmitted || touched.email) && regErrors.email ? ' error' : '')}>
                  <label htmlFor="reg-email">Email</label>
                  <i className="field-icon far fa-envelope"></i>
                  <Form.Control
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={e => setRegEmail(e.target.value)}
                    onBlur={() => markTouched('email')}
                    placeholder=" "
                    aria-label="Email"
                    autoComplete="email"
          required
                  />
                </div>
  {(regSubmitted || touched.email) && regErrors.email && !['Required'].includes(regErrors.email) && <div className="field-error">{regErrors.email}</div>}</div>
                {/* Password */}
  <div className="field-wrapper pw-field-block">
    <div ref={fieldRefs.password} className={"floating-field has-toggle " + (regPassword ? 'is-filled' : '') + ((regSubmitted || touched.password) && regErrors.password ? ' error' : '')}>
      <label htmlFor="reg-password">Password</label>
      <i className="field-icon fas fa-lock"></i>
      <Form.Control
        id="reg-password"
        type={showPassword ? 'text' : 'password'}
        value={regPassword}
        onChange={e => setRegPassword(e.target.value)}
        onBlur={() => { markTouched('password'); }}
        placeholder=" "
        aria-label="Password"
        required
        minLength={8}
        pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z0-9]).{8,}$"
      />
      <button
        type="button"
        className="toggle-visibility"
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={() => setShowPassword(v => !v)}
      >
        <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
      </button>
    </div>
  {(regSubmitted || touched.password) && regErrors.password && !['Required'].includes(regErrors.password) && <div className="field-error">{regErrors.password}</div>}
  </div>
                </div>{/* end first-block */}
                {/* Address */}
  <div ref={fieldRefs.address} className={"floating-field wide " + (regAddress ? 'is-filled' : '') + ((regSubmitted || touched.address) && regErrors.address ? ' error' : '')}>
                  <label htmlFor="reg-address">Address</label>
                  <i className="field-icon fas fa-map-marker-alt"></i>
                  <Form.Control
                    id="reg-address"
                    type="text"
                    value={regAddress}
                    onChange={e => setRegAddress(e.target.value)}
                    onBlur={() => markTouched('address')}
                    placeholder=" "
                    aria-label="Address"
          required
                  />
                </div>
  {(regSubmitted || touched.address) && regErrors.address && !['Required'].includes(regErrors.address) && <div className="field-error">{regErrors.address}</div>}
                {/* City */}
  <div ref={fieldRefs.city} className={"floating-field " + (regCity ? 'is-filled' : '') + ((regSubmitted || touched.city) && regErrors.city ? ' error' : '')}>
                  <label htmlFor="reg-city">City</label>
                  <i className="field-icon fas fa-city"></i>
                  <Form.Control
                    id="reg-city"
                    type="text"
                    value={regCity}
                    onChange={e => setRegCity(e.target.value)}
                    onBlur={() => markTouched('city')}
                    placeholder=" "
                    aria-label="City"
          required
                  />
                </div>
  {(regSubmitted || touched.city) && regErrors.city && !['Required'].includes(regErrors.city) && <div className="field-error">{regErrors.city}</div>}
                {/* State */}
  <div ref={fieldRefs.state} className={"floating-field " + (regState ? 'is-filled' : '') + ((regSubmitted || touched.state) && regErrors.state ? ' error' : '')}>
                  <label htmlFor="reg-state">State / Province</label>
                  <i className="field-icon fas fa-map"></i>
                  <Form.Control
                    id="reg-state"
                    type="text"
                    value={regState}
                    onChange={e => setRegState(e.target.value)}
                    onBlur={() => markTouched('state')}
                    placeholder=" "
                    aria-label="State or Province"
          required
                  />
                </div>
  {(regSubmitted || touched.state) && regErrors.state && !['Required'].includes(regErrors.state) && <div className="field-error">{regErrors.state}</div>}
                {/* Country */}
  <div ref={fieldRefs.country} className={"floating-field " + (regCountry ? 'is-filled' : '') + ((regSubmitted || touched.country) && regErrors.country ? ' error' : '')}>
                  <label htmlFor="reg-country">Country</label>
                  <i className="field-icon fas fa-globe"></i>
                  <Form.Control
                    id="reg-country"
                    type="text"
                    value={regCountry}
                    onChange={e => setRegCountry(e.target.value)}
                    onBlur={() => markTouched('country')}
                    placeholder=" "
                    aria-label="Country"
          required
                  />
                </div>
  {(regSubmitted || touched.country) && regErrors.country && !['Required'].includes(regErrors.country) && <div className="field-error">{regErrors.country}</div>}
                {/* Pin */}
  <div ref={fieldRefs.pin} className={"floating-field " + (regPin ? 'is-filled' : '') + ((regSubmitted || touched.pin) && regErrors.pin ? ' error' : '')}>
                  <label htmlFor="reg-pin">Postal / Pin Code</label>
                  <i className="field-icon fas fa-mail-bulk"></i>
                  <Form.Control
                    id="reg-pin"
                    type="text"
                    value={regPin}
                    onChange={e => setRegPin(e.target.value)}
                    onBlur={() => markTouched('pin')}
                    placeholder=" "
                    aria-label="Postal Code"
          required
                  />
                </div>
  {(regSubmitted || touched.pin) && regErrors.pin && !['Required'].includes(regErrors.pin) && <div className="field-error">{regErrors.pin}</div>}
                {/* Account Type */}
  <div ref={fieldRefs.accountType} className={"floating-field select-field " + (regAccountType ? 'is-filled' : '') + ((regSubmitted || touched.accountType) && regErrors.accountType ? ' error' : '')}>
                  <label htmlFor="reg-account-type">Account Type</label>
                  <i className="field-icon fas fa-briefcase"></i>
                  <Form.Select
                    id="reg-account-type"
                    value={regAccountType}
                    onChange={e => setRegAccountType(e.target.value)}
                    onBlur={() => markTouched('accountType')}
                    aria-label="Account Type"
          required
                  >
                    <option value="" disabled> Account Type - Choose type</option>
                    <option>Retailer</option>
                    <option>Exporter</option>
                    <option>Jewellery Manufacturer</option>
                    <option>Diamond Manufacturer</option>
                    <option>Broker</option>
                  </Form.Select>
                </div>
  {(regSubmitted || touched.accountType) && regErrors.accountType && !['Required'].includes(regErrors.accountType) && <div className="field-error">{regErrors.accountType}</div>}
                {/* Reference */}
  <div ref={fieldRefs.reference} className={"floating-field " + (regReference ? 'is-filled' : '') + ((regSubmitted || touched.reference) && regErrors.reference ? ' error' : '')}>
                  <label htmlFor="reg-reference">How do you know us?</label>
                  <i className="field-icon fas fa-user-friends"></i>
                  <Form.Control
                    id="reg-reference"
                    type="text"
                    value={regReference}
                    onChange={e => setRegReference(e.target.value)}
                    onBlur={() => markTouched('reference')}
                    placeholder=" "
                    aria-label="Reference"
          required
                  />
                </div>
  {(regSubmitted || touched.reference) && regErrors.reference && !['Required'].includes(regErrors.reference) && <div className="field-error">{regErrors.reference}</div>}
                {/* KYC Document (combined row: select + file input) */}
                <div className="doc-block">
                  <div
                    ref={fieldRefs.kycType}
                    className={
                      "doc-combo " +
                      ((regSubmitted || touched.kycType || touched.kycFile) && (regErrors.kycType || regErrors.kycFile) ? ' error' : '')
                    }
                  >
                    <label className="doc-combo-label" htmlFor="reg-kyc-doc-type">Document For KYC</label>
                    <div className="doc-combo-row">
                      <div className="doc-select-wrap">
                        <Form.Select
                          id="reg-kyc-doc-type"
                          value={regKycDocType}
                          onChange={e => setRegKycDocType(e.target.value)}
                          onBlur={() => markTouched('kycType')}
                          aria-label="KYC Document Type"
                          required
                        >
                          <option value="" disabled> Select document for KYC</option>
                          <option value="aadhaar">aadhaar card</option>
                          <option value="pan">PAN card</option>
                          <option value="driver_license">Drivers license</option>
                          <option value="passport">Passport</option>
                        </Form.Select>
                        <i className="chevron fas fa-chevron-down" aria-hidden="true"></i>
                      </div>
                      <input
                        ref={fieldRefs.kycFile as any}
                        type="file"
                        className="doc-file-input"
                        onChange={e => { setRegKycFile(e.target.files ? e.target.files[0] : null); markTouched('kycFile'); }}
                        onBlur={() => markTouched('kycFile')}
                        aria-label="Upload KYC Document"
                        required
                      />
                    </div>
                  </div>
                  {(regSubmitted || regKycDocType || regKycFile) && (regErrors.kycType || regErrors.kycFile) && !['Required','File required'].includes(regErrors.kycType || regErrors.kycFile) && (
                    <div className="field-error">{regErrors.kycType || regErrors.kycFile}</div>
                  )}
                </div>
                {/* Business Proof */}
                <div className="doc-block">
                  <div
                    ref={fieldRefs.bizType}
                    className={
                      "doc-combo " +
                      ((regSubmitted || touched.bizType || touched.bizFile) && (regErrors.bizType || regErrors.bizFile) ? ' error' : '')
                    }
                  >
                    <label className="doc-combo-label" htmlFor="reg-biz-doc-type">Document For Business Proof</label>
                    <div className="doc-combo-row">
                      <div className="doc-select-wrap">
                        <Form.Select
                          id="reg-biz-doc-type"
                          value={regBizDocType}
                          onChange={e => setRegBizDocType(e.target.value)}
                          onBlur={() => markTouched('bizType')}
                          aria-label="Business Proof Document Type"
                          required
                        >
                          <option value="" disabled> Select document for Business Proof</option>
                          <option value="gst_certificate">GST certificate</option>
                          <option value="business_pan">Business PAN card</option>
                          <option value="other_document">Other Govt issued Id</option>
                        </Form.Select>
                        <i className="chevron fas fa-chevron-down" aria-hidden="true"></i>
                      </div>
                      <input
                        ref={fieldRefs.bizFile as any}
                        type="file"
                        className="doc-file-input"
                        onChange={e => { setRegBizFile(e.target.files ? e.target.files[0] : null); markTouched('bizFile'); }}
                        onBlur={() => markTouched('bizFile')}
                        aria-label="Upload Business Proof"
                        required
                      />
                    </div>
                  </div>
                  {(regSubmitted || regBizDocType || regBizFile) && (regErrors.bizType || regErrors.bizFile) && !['Required','File required'].includes(regErrors.bizType || regErrors.bizFile) && (
                    <div className="field-error">{regErrors.bizType || regErrors.bizFile}</div>
                  )}
                </div>
                <div ref={fieldRefs.agree} className={"agree-row d-flex align-items-center mb-3" + ((regSubmitted || !regAgree) && regErrors.agree ? ' error' : '')}>
                    <Form.Check
                    id="reg-agree"
                    type="checkbox"
                    checked={regAgree}
                    onChange={e => setRegAgree(e.target.checked)}
                    label={<span>I have read and agree to the <button type="button" className="link-like small" style={{fontSize:'0.7rem'}}>Terms &amp; Conditions*</button></span>}
                    />
                </div>
              </div>

  {(regSubmitted || regAgree === false) && regErrors.agree && !['Must agree'].includes(regErrors.agree) && <div className="field-error mb-2">{regErrors.agree}</div>}
  <Button type="submit" className="section-btn w-100 mb-2" disabled={!regAgree || hasRegErrors || registerLoading}>
                <span className="btn-content">{registerLoading ? 'Submitting...' : registerSuccess ? 'Submitted' : 'Register'}</span>
              </Button>
              <div className="text-center">
                <button type="button" className="link-like" onClick={() => { setShowRegister(false); }}>Back to Sign in</button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </section>
  );
};

export default LoginPage;
